import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Input, Select, Tooltip, Typography } from 'antd'
import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import { icons, Spacer, Spinner } from 'portal/lib/primitives'
import { useAutodeskLoader } from './useAutodeskLoader'
import { useAutodeskProjectsLoader } from './useAutodeskProjectsLoader'

export const AutodeskConfiguration = ({
  autodeskConfiguration,
  setAutodeskConfiguration,
  startingHubId,
  saving,
  resetKey = 0,
  setResetKey
}) => {
  const [localHubId, setLocalHubId] = useState('')
  const [copiedText, setCopiedText] = useState('')

  useEffect(() => {
    setLocalHubId(startingHubId || '')
  }, [startingHubId])

  const { loading, error, data } = useAutodeskLoader()
  const {
    loading: projectsLoading,
    error: projectsError,
    data: projectsData,
    reload: reloadProjects
  } = useAutodeskProjectsLoader(localHubId)

  useEffect(() => {
    if (resetKey > 0) {
      reloadProjects(autodeskConfiguration?.hubId)
      setResetKey(0)
    }
  }, [resetKey, reloadProjects, autodeskConfiguration?.hubId, setResetKey])

  const projectOptions = useMemo(
    () =>
      projectsData?.projects?.map(project => ({
        label: project.name,
        value: project.id,
        title: ''
      })),
    [projectsData]
  )

  // On change of any of the fields, we want to update the autodesk configuration in the parent component (Integrations) so that it can be saved when the user clicks "Save Changes"
  const handleFieldChange = (name, value) => {
    // If HubId changes, we want to reset the ProjectId as well
    if (name === 'hubId' && value !== autodeskConfiguration?.hubId) {
      setAutodeskConfiguration({
        ...autodeskConfiguration,
        hubId: value,
        projectId: null
      })
      return
    }
    setAutodeskConfiguration({
      ...autodeskConfiguration,
      [name]: value
    })
  }

  // If HubId changes, we want to update the localHubId which will update the projects dropdown.
  const handleHubIdBlur = value => {
    setLocalHubId(value)
  }

  type ErrorMessages = JSX.Element[]

  const getErrorMessages = useCallback(() => {
    const messages: ErrorMessages = []
    if (projectsError && projectsError.errors) {
      // For each key in the errors object, if value is an array,
      // add each item in the array to the messages array as a <div>.
      Object.keys(projectsError.errors).forEach(key => {
        const value = projectsError.errors[key]
        if (Array.isArray(value)) {
          value.forEach((item: string, index: number) => {
            messages.push(<div key={`${key}-${index}`}>{item}</div>)
          })
        } else if (typeof value === 'string') {
          messages.push(<div key={key}>{value}</div>)
        }
      })
    }

    return messages
  }, [projectsError])

  const { Link } = Typography

  return (
    <ConfigurationContainer>
      <TextArea>
        <Description>Autodesk Configuration</Description>
        <AdditionalText>
          To enable the integration, an admin user must add a custom integration
          at the Hub level using the provided Client ID.
        </AdditionalText>
        <AdditionalText>
          At the Project level, a member should be added using the Service
          Account Email shown with the member permissions added for Build to
          allow issue creation.
        </AdditionalText>
        <AdditionalText>
          <Link
            href="https://qualisflow.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colours.TEAL, fontSize: '0.75rem' }}
          >
            Privacy policy
          </Link>
        </AdditionalText>
      </TextArea>
      <SettingArea>
        <FormContainer>
          {loading && <Spinner />}
          {error && <span>An error has occurred</span>}
          {!loading && !error && data && (
            <>
              <div>
                <FormLabel>Client ID</FormLabel>
                <ReadOnlyWrapper>
                  <ReadOnlyText>{data.clientId}</ReadOnlyText>
                  <div
                    style={{
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(data.clientId)
                      setCopiedText(data.clientId)
                      setTimeout(() => {
                        setCopiedText('')
                      }, 2000)
                    }}
                  >
                    <Tooltip title="Copy to clipboard">
                      <icons.CopyToClipbaord id={`autodesk-client-id-copy`} />
                    </Tooltip>
                  </div>
                  {copiedText === data.clientId && (
                    <CopiedText>
                      <icons.CircleTicked size={sizes.SMALL} />
                      <div>Copied</div>
                    </CopiedText>
                  )}
                </ReadOnlyWrapper>
              </div>
              <div>
                <FormLabel>Service Account Email</FormLabel>
                <ReadOnlyWrapper>
                  <ReadOnlyText>{data.serviceAccountEmail}</ReadOnlyText>
                  <div
                    style={{
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(data.serviceAccountEmail)
                      setCopiedText(data.serviceAccountEmail)
                      setTimeout(() => {
                        setCopiedText('')
                      }, 2000)
                    }}
                  >
                    <Tooltip title="Copy to clipboard">
                      <icons.CopyToClipbaord
                        id={`autodesk-service-account-email-copy`}
                      />
                    </Tooltip>
                  </div>
                  {copiedText === data.serviceAccountEmail ? (
                    <CopiedText>
                      <icons.CircleTicked size={sizes.SMALL} />
                      <div>Copied</div>
                    </CopiedText>
                  ) : (
                    <Spacer.Larger />
                  )}
                </ReadOnlyWrapper>
              </div>
              <div>
                <FormLabel>Hub ID</FormLabel>
                <Input
                  value={autodeskConfiguration?.hubId}
                  placeholder="Enter Hub ID"
                  style={{ width: '418px' }}
                  onChange={e => handleFieldChange('hubId', e.target.value)}
                  onBlur={e => handleHubIdBlur(e.target.value)}
                  disabled={saving}
                />
              </div>
              {projectsError && (
                <ErrorContainer>
                  <icons.CrossCircle
                    size={sizes.SMALL_MEDIUM}
                    colour={colours.RED}
                  />
                  {getErrorMessages()}
                </ErrorContainer>
              )}
              <div>
                <FormLabel>Project</FormLabel>
                <Tooltip
                  title={
                    !projectsData ||
                    !projectsData.projects ||
                    projectsError ||
                    projectsData.projects.length === 0
                      ? 'No projects available for this hub'
                      : ''
                  }
                >
                  <Select
                    key={
                      projectOptions
                        ? projectOptions.map(o => o.value).join(',')
                        : ''
                    } // changes when options change
                    value={autodeskConfiguration?.projectId || undefined}
                    placeholder="Select project"
                    style={{ width: '418px' }}
                    labelRender={option => option?.label || ''}
                    options={projectOptions}
                    loading={projectsLoading}
                    disabled={
                      saving ||
                      projectsLoading ||
                      !autodeskConfiguration?.hubId ||
                      !projectsData ||
                      !projectsData.projects ||
                      projectsError ||
                      projectsData.projects.length === 0
                    }
                    onSelect={value => handleFieldChange('projectId', value)}
                  />
                </Tooltip>
              </div>
              <div>
                <FormLabel>Project Region</FormLabel>
                <Select
                  value={autodeskConfiguration?.region}
                  placeholder="Select region"
                  style={{ width: '418px', marginBottom: '0.5rem' }}
                  labelRender={option => option?.label || ''}
                  options={data.regions?.map(region => ({
                    label: region.name,
                    value: region.value,
                    title: ''
                  }))}
                  onSelect={value => handleFieldChange('region', value)}
                  disabled={saving || !autodeskConfiguration?.projectId}
                />
              </div>
            </>
          )}
        </FormContainer>
      </SettingArea>
    </ConfigurationContainer>
  )
}

export const ConfigurationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 3.25rem;
`

export const TextArea = styled.div`
  min-width: 270px;
  max-width: 270px;
`
export const SettingArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  gap: 0.5rem;
  width: 100%;
`
export const Description = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
`
export const AdditionalText = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colours.MID_GREY};
  line-height: 1rem;
  margin-top: 0.5rem;
`
export const AdditionalTextAddendum = styled.div`
  font-size: 0.65rem;
  font-style: italic;
  color: ${colours.MID_GREY};
  margin-top: 0.5rem;
`
const FormContainer = styled.div`
  background-color: ${colours.WHITE};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid ${colours.CLOUD_GREY};
  padding: 0.75rem;
  margin-top: 0.25rem;
  min-width: 442px;
`
const FormLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`
const ReadOnlyWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`
const ReadOnlyText = styled.div`
  color: ${colours.MID_GREY};
  font-size: 0.875rem;
  font-weight: 400;
  word-break: break-all;
`
const ErrorContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  color: ${colours.CORE_NAVY};
  background-color: ${colours.RED}11;
  border-radius: 5px;
  border: 1px solid ${colours.RED}22;
  padding: 0.25rem 1rem 0.25rem 0.75rem;
  width: 418px;
`
const CopiedText = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colours.GREEN};
  display: flex;
  align-items: center;
  gap: 0.125rem;

  & > div {
    line-height: 0.75rem;
  }
`
